'use client';

import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { forwardRef } from 'react';

import { cn } from '../lib/cn';
import { ChatbotIcon } from './ChatbotIcon';

export type ChatbotButtonVariant = 'default' | 'pill';

interface ChatbotButtonProps {
  isOpen: boolean;
  onClick: () => void;
  variant?: ChatbotButtonVariant;
}

export const ChatbotButton = forwardRef<HTMLButtonElement, ChatbotButtonProps>(
  ({ isOpen, onClick, variant = 'default' }, ref) => {
    if (variant === 'pill') {
      return (
        <motion.button
          ref={ref}
          onClick={onClick}
          data-chatbot-button
          className={cn(
            'inline-flex items-center justify-center whitespace-nowrap rounded-[20px] border border-solid border-white bg-black px-2 py-1',
            'text-[12px] font-bold leading-[14px] text-white',
            'cursor-pointer transition-colors hover:bg-g-90',
            'focus:outline-none',
            isOpen && 'max-mobile:hidden',
          )}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label={isOpen ? '챗봇 닫기' : '챗봇 열기'}
        >
          {isOpen ? '챗봇닫기' : '챗봇상담'}
        </motion.button>
      );
    }

    return (
      <motion.button
        ref={ref}
        onClick={onClick}
        data-chatbot-button
        className={cn(
          'relative flex h-[46px] w-[46px] items-center justify-center rounded-full bg-g-90 text-g-00',
          'shadow-[10px_10px_30px_0px_rgba(18,18,18,0.2)]',
          'cursor-pointer border-none transition-colors hover:bg-g-80',
          'focus:outline-none',
          isOpen && 'max-mobile:hidden',
        )}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={isOpen ? '챗봇 닫기' : '챗봇 열기'}
      >
        <motion.div
          initial={false}
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
        >
          {isOpen ? <X className="h-5 w-5 text-g-00" /> : <ChatbotIcon size={24} />}
        </motion.div>

      </motion.button>
    );
  },
);

ChatbotButton.displayName = 'ChatbotButton';
